import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, CalendarCheck, Phone, AlertCircle, Wind, BarChart3,
  Sparkles, Stethoscope, Users, Plug, CheckCircle2,
} from "lucide-react";
import { useDemo } from "@/components/landing/DemoContext";
import { trackEvent } from "@/access/tracker";

const KICKER = "font-display text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground";

/* ─── Diagnostic answers ─── */
const ANSWERS = [
  {
    id: "calls", persona: "comms", Icon: Phone,
    accent: "from-rose-500 to-orange-500", ring: "border-rose-500/40", chip: "text-rose-700 dark:text-rose-300",
    question: "Too many calls hit voicemail.",
    hint: "Front desk overwhelmed. Patients leaving. Voicemails piling up.",
    answer: {
      headline: "We answer the calls your team can\u2019t.",
      body: "AI Receptionist picks up in under 3 rings, qualifies the lead, and books straight into your EMR — 24/7. Your front desk only hears about the calls that need them.",
      stat: { value: "97%", label: "Inbound calls answered. Including 11pm Sundays." },
      cta: { label: "See the AI Receptionist", to: "/ai-workforce" },
    },
  },
  {
    id: "noshow", persona: "scale", Icon: AlertCircle,
    accent: "from-amber-500 to-rose-500", ring: "border-amber-500/40", chip: "text-amber-700 dark:text-amber-300",
    question: "Patients no-show more than they book.",
    hint: "Empty chairs eating margin. Reactivation nobody has time for.",
    answer: {
      headline: "We rebook the chairs you\u2019re losing.",
      body: "Sales Autopilot nudges no-shows back to the calendar automatically. Reactivation flows wake up dormant patients. Your team never opens a spreadsheet to chase a recall again.",
      stat: { value: "$8.4k / mo", label: "Average revenue recovered, per clinic." },
      cta: { label: "See Sales Autopilot", to: "/products/sales-autopilot" },
    },
  },
  {
    id: "tools", persona: "calm", Icon: Wind,
    accent: "from-emerald-500 to-teal-500", ring: "border-emerald-500/40", chip: "text-emerald-700 dark:text-emerald-300",
    question: "My team is drowning in 12 tools.",
    hint: "Different login for every job. Nothing talks to anything else.",
    answer: {
      headline: "We replace the stack. We don\u2019t add to it.",
      body: "Jane, Juvonno, ClinicSense — your EMR stays. Everything else (calls, comms, marketing, reviews, recalls, AI) collapses into one screen. We migrate the tools you\u2019re tired of, with you.",
      stat: { value: "11 hrs / wk", label: "Returned to your front-desk team." },
      cta: { label: "See the platform", to: "/platform" },
    },
  },
  {
    id: "numbers", persona: "data", Icon: BarChart3,
    accent: "from-blue-500 to-sky-500", ring: "border-blue-500/40", chip: "text-blue-700 dark:text-blue-300",
    question: "I can\u2019t see the numbers I need.",
    hint: "Owner asks for revenue, EMR exports take a week. Decisions made blind.",
    answer: {
      headline: "Live numbers. Traceable. Yours.",
      body: "Clinic Pulse rolls up revenue, no-show rate, recall conversion and AI activity in real time — across one location or ten. Every number traces back to its EMR source row.",
      stat: { value: "live", label: "Visibility. Compared to weekly EMR exports." },
      cta: { label: "See Clinic Pulse", to: "/juvonno/clinicpulse" },
    },
  },
];

/* ────────────────────────────────────────────────────────────────
   WelcomeHero — light, clear, friendly. Adds a tiny step-counter
   so the visitor sees the flow they&rsquo;re about to walk through.
   ──────────────────────────────────────────────────────────────── */
const WelcomeHero = () => {
  const openDemo = useDemo();
  return (
    <section data-testid="welcome-hero" className="relative isolate overflow-hidden pt-28 pb-12 sm:pt-36 sm:pb-16">
      <div className="pointer-events-none absolute -top-20 left-1/2 -z-10 h-[420px] w-[760px] -translate-x-1/2 rounded-full bg-violet-500/[0.06] blur-[140px]" />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="flex items-center gap-3"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-foreground/15 bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/80">
            <Sparkles className="h-3 w-3 text-violet-500" />
            Self-guided tour · 2 mins
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.05 }}
          data-testid="welcome-h1"
          className="mt-6 max-w-4xl font-display text-[2.25rem] font-black leading-[1] tracking-[-0.03em] sm:text-[3.25rem] lg:text-[4.25rem]"
        >
          Tell us what&rsquo;s slowing your clinic down.{" "}
          <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">We&rsquo;ll show you the fix.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="mt-5 max-w-2xl text-[15px] leading-relaxed text-muted-foreground sm:text-[17px]"
        >
          Pick the bottleneck below that sounds most like yours. You&rsquo;ll see exactly how Clinic OS clears it — no form, no deck, no commit.
        </motion.p>

        {/* Tiny flow indicator — sets expectations */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.25 }}
          className="mt-8 flex flex-wrap items-center gap-3 text-[12.5px] text-muted-foreground"
        >
          <span className="inline-flex items-center gap-2"><Step n="1" />Pick your bottleneck</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground/50" />
          <span className="inline-flex items-center gap-2"><Step n="2" />See the fix &amp; numbers</span>
          <ArrowRight className="h-3 w-3 text-muted-foreground/50" />
          <span className="inline-flex items-center gap-2"><Step n="3" />Book or keep exploring</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.32 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row"
        >
          <a
            href="#welcome-diagnostic"
            data-testid="welcome-hero-start"
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-foreground px-7 text-[14.5px] font-bold text-background shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg sm:text-[15px]"
          >
            Start the diagnostic
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
          <button
            type="button"
            data-testid="welcome-hero-book"
            onClick={openDemo}
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-foreground/15 bg-card px-6 text-[14.5px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30 sm:text-[15px]"
          >
            <CalendarCheck className="h-4 w-4" />
            Or talk to a human now
          </button>
        </motion.div>
      </div>
    </section>
  );
};

const Step = ({ n }) => (
  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-foreground text-[10.5px] font-bold text-background">{n}</span>
);

/* ────────────────────────────────────────────────────────────────
   Diagnostic — single question, vertical conversational picks
   ──────────────────────────────────────────────────────────────── */
const DiagnosticBlock = () => {
  const [activeId, setActiveId] = useState(null);
  const navigate = useNavigate();
  const openDemo = useDemo();
  const active = ANSWERS.find((a) => a.id === activeId);

  const onPick = (a) => {
    setActiveId(a.id);
    trackEvent("welcome_diagnostic_pick", {
      persona: a.persona,
      meta: { id: a.id, from_path: "/welcome" },
    });
    setTimeout(() => {
      document.getElementById("answer-reveal")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <section id="welcome-diagnostic" data-testid="welcome-diagnostic" className="relative scroll-mt-24 bg-secondary/30 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="mx-auto max-w-3xl">
          <p className={KICKER}>Step 1 — Pick your bottleneck</p>
          <h2 className="mt-3 font-display text-[1.875rem] font-black leading-[1.04] tracking-[-0.025em] sm:text-[2.5rem] lg:text-[3rem]">
            Pick the one that&rsquo;s{" "}
            <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">eating your week.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
            Tap the line that sounds most like you. We&rsquo;ll reveal the fix instantly — no form required.
          </p>
        </div>

        <ul className="mx-auto mt-10 max-w-3xl space-y-3 sm:mt-12 sm:space-y-4">
          {ANSWERS.map((a) => {
            const Ic = a.Icon;
            const isActive = activeId === a.id;
            return (
              <li key={a.id}>
                <button
                  type="button"
                  data-testid={`diagnostic-pick-${a.id}`}
                  onClick={() => onPick(a)}
                  className={`group relative flex w-full items-start gap-4 overflow-hidden rounded-2xl border bg-card px-5 py-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md sm:gap-5 sm:px-6 sm:py-6 ${a.ring} ${isActive ? "ring-2 ring-foreground/15" : ""}`}
                >
                  <span className={`mt-0.5 inline-flex h-11 w-11 flex-none items-center justify-center rounded-xl bg-gradient-to-br ${a.accent} text-white shadow-sm ring-1 ring-inset ring-white/15`}>
                    <Ic className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-[16px] font-black leading-tight tracking-tight text-foreground sm:text-[17.5px]">
                      {a.question}
                    </p>
                    <p className="mt-1.5 text-[12.5px] leading-snug text-muted-foreground sm:text-[13px]">
                      {a.hint}
                    </p>
                  </div>
                  <ArrowRight className={`mt-1 h-4 w-4 flex-none transition-all group-hover:translate-x-0.5 ${isActive ? a.chip : "text-muted-foreground"}`} />
                </button>
              </li>
            );
          })}
        </ul>

        {/* Answer reveal */}
        <div id="answer-reveal" className="mx-auto mt-12 max-w-3xl scroll-mt-24">
          <AnimatePresence mode="wait">
            {active && (
              <motion.div
                key={active.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.32 }}
                data-testid="diagnostic-answer"
                className={`relative overflow-hidden rounded-3xl border bg-card p-6 shadow-md sm:p-10 ${active.ring}`}
              >
                <div className={`pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-gradient-to-br opacity-25 blur-3xl ${active.accent}`} />

                <p className={`font-display text-[11px] font-bold uppercase tracking-[0.22em] ${active.chip}`}>
                  Step 2 — Here&rsquo;s what changes
                </p>
                <h3 className="mt-3 font-display text-[1.625rem] font-black leading-[1.05] tracking-[-0.025em] sm:text-[2.125rem]">
                  {active.answer.headline}
                </h3>
                <p className="mt-4 max-w-2xl text-[14px] leading-relaxed text-muted-foreground sm:text-[15px]">
                  {active.answer.body}
                </p>

                <div className="mt-7 grid items-end gap-6 sm:grid-cols-[1fr_auto]">
                  <div>
                    <p className={`font-display text-[3.25rem] font-black leading-none tracking-[-0.04em] sm:text-[4rem] bg-gradient-to-br bg-clip-text text-transparent ${active.accent}`}>
                      {active.answer.stat.value}
                    </p>
                    <p className="mt-2 max-w-sm text-[12.5px] leading-relaxed text-muted-foreground sm:text-[13px]">
                      {active.answer.stat.label}
                    </p>
                  </div>

                  <div className="flex flex-col gap-2 sm:flex-row">
                    <button
                      type="button"
                      data-testid="answer-book-demo"
                      onClick={openDemo}
                      className="group inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-foreground px-5 text-[13.5px] font-bold text-background transition-all hover:-translate-y-0.5"
                    >
                      <CalendarCheck className="h-4 w-4" />
                      Book a 20-min demo
                    </button>
                    <button
                      type="button"
                      data-testid="answer-deep-dive"
                      onClick={() => navigate(active.answer.cta.to)}
                      className="group inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-foreground/15 bg-background px-4 text-[13px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30"
                    >
                      {active.answer.cta.label}
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  </div>
                </div>

                <p className="mt-6 inline-flex items-center gap-2 text-[12px] text-muted-foreground">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  HIPAA-ready · 22+ clinics live · Starts at $297/mo
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

/* ────────────────────────────────────────────────────────────────
   Bridge — light theme, EMR ↔ Clinic OS ↔ Patient.
   ──────────────────────────────────────────────────────────────── */
const Bridge = () => {
  return (
    <section data-testid="welcome-bridge" className="relative py-20 sm:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        <div className="grid items-end gap-6 lg:grid-cols-[1fr_1fr]">
          <div>
            <p className={KICKER}>The core idea</p>
            <h2 className="mt-2 font-display text-[1.875rem] font-black leading-[1.04] tracking-[-0.025em] sm:text-[2.5rem] lg:text-[3rem]">
              We sit between your EMR{" "}
              <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">and your patients.</span>
            </h2>
          </div>
          <p className="text-[14.5px] leading-relaxed text-muted-foreground sm:text-[15.5px]">
            You don&rsquo;t switch EMRs. You don&rsquo;t bolt on six more tools. Clinic OS is the calm layer in the middle — calls, comms, AI, reviews, recalls, marketing — talking 2-way to the EMR you already run.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 items-stretch gap-4 sm:mt-16 sm:grid-cols-[1fr_auto_1.25fr_auto_1fr] sm:gap-3">
          <div data-testid="bridge-emr" className="rounded-2xl border border-blue-500/30 bg-card p-5 text-center sm:p-6">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-sm">
              <Stethoscope className="h-5 w-5" />
            </span>
            <p className="mt-3 font-display text-[15px] font-black tracking-tight">Your EMR</p>
            <p className="mt-1 text-[11.5px] text-muted-foreground">Jane · Juvonno · ClinicSense</p>
          </div>

          <Connector />

          <div data-testid="bridge-clinic-os" className="relative overflow-hidden rounded-2xl border-2 border-foreground/15 bg-card p-6 text-center shadow-lg sm:p-7">
            <div className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-rose-500/15 blur-3xl" />
            <span className="relative inline-flex h-13 w-13 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 p-3 text-white shadow-md">
              <Sparkles className="h-5 w-5" />
            </span>
            <p className="relative mt-3 font-display text-[17px] font-black tracking-tight">Clinic OS</p>
            <p className="relative mt-1 text-[11.5px] text-muted-foreground">Calls · comms · AI · reviews · recalls</p>
          </div>

          <Connector />

          <div data-testid="bridge-patient" className="rounded-2xl border border-emerald-500/30 bg-card p-5 text-center sm:p-6">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 text-white shadow-sm">
              <Users className="h-5 w-5" />
            </span>
            <p className="mt-3 font-display text-[15px] font-black tracking-tight">Your patients</p>
            <p className="mt-1 text-[11.5px] text-muted-foreground">Calls · SMS · DMs · reviews</p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Connector = () => (
  <div className="flex items-center justify-center sm:px-2">
    <div className="hidden sm:block relative h-px w-10 overflow-hidden bg-border">
      <motion.span
        initial={{ x: "-100%" }}
        animate={{ x: "200%" }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-foreground/40 to-transparent"
      />
    </div>
    <Plug className="block sm:hidden h-4 w-4 text-muted-foreground/60" />
  </div>
);

/* ────────────────────────────────────────────────────────────────
   ClosingNote — light theme. Friendly, low-pressure closer.
   ──────────────────────────────────────────────────────────────── */
const ClosingNote = () => {
  const openDemo = useDemo();
  return (
    <section data-testid="welcome-closing" className="relative bg-secondary/40 py-20 sm:py-24">
      <div className="relative mx-auto max-w-3xl px-5 text-center sm:px-8">
        <p className={KICKER}>Step 3 — When you&rsquo;re ready</p>
        <h2 className="mt-3 font-display text-[2rem] font-black leading-[1.04] tracking-[-0.025em] sm:text-[2.75rem] lg:text-[3.25rem]">
          Twenty minutes. No deck.{" "}
          <span className="bg-gradient-to-br from-amber-500 via-rose-500 to-fuchsia-500 bg-clip-text text-transparent">No pressure.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-[14.5px] leading-relaxed text-muted-foreground sm:text-[15.5px]">
          A walkthrough on your numbers, your stack, your specialty. You leave with a clear picture — even if Clinic OS isn&rsquo;t the right fit.
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <button
            type="button"
            data-testid="welcome-closing-demo"
            onClick={openDemo}
            className="group inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-foreground px-7 text-[14.5px] font-bold text-background shadow-md transition-all hover:-translate-y-0.5 hover:shadow-lg sm:text-[15px]"
          >
            <CalendarCheck className="h-4 w-4" />
            Book a 20-min demo
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </button>
          <Link
            to="/platform"
            data-testid="welcome-closing-platform"
            className="inline-flex h-12 items-center justify-center gap-2 rounded-xl border border-foreground/15 bg-card px-6 text-[14.5px] font-semibold transition-all hover:-translate-y-0.5 hover:border-foreground/30 sm:text-[15px]"
          >
            See the platform
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <p className="mt-6 inline-flex items-center gap-2 text-[12px] text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
          HIPAA-ready · 22+ clinics live · Starts at $297/mo · Cancel anytime
        </p>
      </div>
    </section>
  );
};

export default function Welcome() {
  return (
    <>
      <WelcomeHero />
      <DiagnosticBlock />
      <Bridge />
      <ClosingNote />
    </>
  );
}
